import React, { useState, useEffect } from "react";
import axios from "axios";
import { getAuth } from "firebase/auth";
import { getFirestore, collection, getDocs, addDoc, serverTimestamp, doc, getDoc, updateDoc } from "firebase/firestore";
import "./ServiceRequestForm.css";

const ServiceRequestForm = () => {
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [description, setDescription] = useState("");
  const [aiResponse, setAiResponse] = useState("");
  const [loadingAI, setLoadingAI] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const fetchCategories = async () => {
      const db = getFirestore();
      try {
        const querySnapshot = await getDocs(collection(db, "categorias"));
        const fetchedCategories = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setCategories(fetchedCategories);
      } catch (error) {
        console.error("Error al obtener las categorías:", error);
      }
    };

    fetchCategories();
  }, []);

  const handleGenerateInfo = async () => {
    if (!category) {
      setAiResponse("Por favor, selecciona una categoría antes de generar la información.");
      return;
    }
    setLoadingAI(true);
    setAiResponse("");

    // Verificación de saldo y solicitudes previas
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      setErrorMessage("Debes estar autenticado para generar la información.");
      return;
    }

    const db = getFirestore();
    const userDocRef = doc(db, "users", user.uid);
    const userDocSnap = await getDoc(userDocRef);

    if (!userDocSnap.exists()) {
      setErrorMessage("No se pudo encontrar información del usuario.");
      return;
    }

    const userData = userDocSnap.data();
    const solicitudesQuery = await getDocs(collection(db, "solicitudservicios"));
    const userSolicitudes = solicitudesQuery.docs.filter(doc => doc.data().uid === user.uid);

    // Verificar si el usuario ya ha realizado 3 solicitudes
    if (userSolicitudes.length >= 3) {
      // Si ya hizo 3 solicitudes, verificar saldo
      const COSTO_SOLICITUD = 1500;
      if (userData.saldo < COSTO_SOLICITUD) {
        setErrorMessage("Saldo insuficiente. Debes recargar saldo para solicitar más servicios.");
        return; // Salir si no tiene saldo suficiente
      }

      // Restar saldo
      await updateDoc(userDocRef, {
        saldo: userData.saldo - COSTO_SOLICITUD,
      });
    }

    try {
      const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
      const prompt = `Un usuario está solicitando un presupuesto en la categoría "${category}". La descripción que proporcionó es: "${description}". 

      Devuélvele una respuesta detallada que lo ayude a mejorar su solicitud de presupuesto. Incluye:
      - Información clave que debe incluir en su solicitud para obtener un presupuesto preciso.
      - Posibles soluciones previas que puede considerar antes de contratar.
      - Consejos para asegurarse de que el trabajo se realice correctamente.

      La respuesta debe ser clara, concisa, estructurada, de máximo 250 palabras y enfocada en ayudar al usuario en su solicitud dentro de la plataforma, no quiero que menciones el buscar profesionales por fuera del sitio.`;

      const response = await axios.post(
        "https://api.openai.com/v1/chat/completions",
        {
          model: "gpt-3.5-turbo",
          messages: [{ role: "user", content: prompt }],
          max_tokens: 300,
          temperature: 0.7,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
          },
        }
      );

      setAiResponse(response.data.choices[0].message.content);
      setShowRequestForm(true);
    } catch (error) {
      console.error("Error al obtener respuesta de IA:", error);
      setAiResponse("Ocurrió un error al generar la información.");
    } finally {
      setLoadingAI(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!category) {
      setErrorMessage("Debes seleccionar una categoría.");
      return;
    }

    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      setErrorMessage("Debes estar autenticado para solicitar un servicio.");
      return;
    }

    const db = getFirestore();
    const userDocRef = doc(db, "users", user.uid);
    const userDocSnap = await getDoc(userDocRef);

    if (!userDocSnap.exists()) {
      setErrorMessage("No se pudo encontrar información del usuario.");
      return;
    }

    const userData = userDocSnap.data();
    const solicitudesQuery = await getDocs(collection(db, "solicitudservicios"));
    const userSolicitudes = solicitudesQuery.docs.filter(doc => doc.data().uid === user.uid);

    // Verifica si el usuario tiene 3 solicitudes y si su saldo es menor a 1500
    if (userSolicitudes.length >= 3) {
      const COSTO_SOLICITUD = 1500;

      if (userData.saldo < COSTO_SOLICITUD) {
        setErrorMessage("Saldo insuficiente. Debes recargar saldo para solicitar más servicios. El costo de cada solicitud es de $1500");
        return;
      }

      // Restar saldo
      await updateDoc(userDocRef, {
        saldo: userData.saldo - COSTO_SOLICITUD,
      });
    }

    try {
      setLoading(true);

      const solicitudData = {
        category,
        description,
        uid: user.uid,
        cliente: userData.fullName || "Usuario desconocido",
        country: userData.country || "Usuario desconocido",
        state: userData.state || "Usuario desconocido",
        city: userData.city || "Usuario desconocido",
        createdAt: serverTimestamp(),
        estado: "pendiente",
      };

      await addDoc(collection(db, "solicitudservicios"), solicitudData);

      setSuccessMessage("¡Solicitud enviada con éxito!");
      setCategory("");
      setDescription("");
    } catch (error) {
      console.error("Error al guardar la solicitud:", error);
      setErrorMessage("Ocurrió un error al enviar tu solicitud. Intenta nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="service-form">
      <h2>Encuentra el servicio que necesitas</h2>

      <label htmlFor="category">Selecciona una categoría de servicio:</label>
      <select id="category" value={category} onChange={(e) => setCategory(e.target.value)} required>
        <option value="">Selecciona una categoría</option>
        {categories.map((cat) => (
          <option key={cat.id} value={cat.name}>
            {cat.name}
          </option>
        ))}
      </select>

      <label htmlFor="description">Describe lo que necesitas:</label>
      <textarea
        id="description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Escribe los detalles del servicio que necesitas..."
        required
      />

      <button type="button" className="btn btn-secondary" onClick={handleGenerateInfo} disabled={loadingAI}>
        {loadingAI ? "Generando información..." : "Obtener información detallada"}
      </button>

      {aiResponse && (
        <div className="ai-response">
          <h3>Información para tu solicitud:</h3>
          <p>{aiResponse}</p>
        </div>
      )}

      {showRequestForm && (
        <form onSubmit={handleSubmit}>
          <div>
            <small>
              Revisa las sugerencias de Apimys IA, escribe nuevamente el detalle de lo que deseas aplicando las sugerencias y envía el presupuesto con el botón de abajo.
            </small>
          </div> 
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? "Enviando..." : "Solicitar presupuesto"}
          </button>
        </form>
      )}

      {successMessage && <p className="success-message">{successMessage}</p>}
      {errorMessage && <p className="error-message">{errorMessage}</p>}
    </div>
  );
};

export default ServiceRequestForm;
